import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API_URL = '/api/encoders/';

export const fetchEncoders = createAsyncThunk(
  'encoders/fetchEncoders',
  async (_, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
        },
      };
      const { data } = await axios.get(API_URL, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchEncoderStats = createAsyncThunk(
  'encoders/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
        },
      };
      const { data } = await axios.get(`${API_URL}stats/summary`, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createEncoder = createAsyncThunk(
  'encoders/createEncoder',
  async (encoderData, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const { data } = await axios.post(API_URL, encoderData, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const bootstrapEncoder = createAsyncThunk(
  'encoders/bootstrap',
  async (encoderId, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
        },
      };
      const { data } = await axios.post(
        `${API_URL}${encoderId}/bootstrap`,
        {},
        config
      );
      return { encoderId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const checkEncoderHealth = createAsyncThunk(
  'encoders/checkHealth',
  async (encoderId, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
        },
      };
      const { data } = await axios.post(
        `${API_URL}${encoderId}/check-health`,
        {},
        config
      );
      return { encoderId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const checkAllEncodersHealth = createAsyncThunk(
  'encoders/checkAllHealth',
  async (_, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
        },
      };
      const { data } = await axios.post(`${API_URL}check-all-health`, {}, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateEncoder = createAsyncThunk(
  'encoders/updateEncoder',
  async ({ encoderId, encoderData }, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const { data } = await axios.put(`${API_URL}${encoderId}`, encoderData, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const encodersSlice = createSlice({
  name: 'encoders',
  initialState: {
    encoders: [],
    stats: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearEncodersError: (state) => {
      state.error = null;
    },
    updateEncoderStatus: (state, action) => {
      // Update encoder online/offline status and version
      const encoder = state.encoders.find(e => e.id === action.payload.id);
      if (encoder) {
        encoder.is_online = action.payload.is_online;
        if (action.payload.name) encoder.name = action.payload.name;
        if (action.payload.ip_address) encoder.ip_address = action.payload.ip_address;
        if (action.payload.version) encoder.agent_version = action.payload.version;
        if (action.payload.agent_version) encoder.agent_version = action.payload.agent_version;
      }
    },
    updateEncoderStats: (state, action) => {
      // Update encoder statistics
      const encoder = state.encoders.find(e => e.id === action.payload.id);
      if (encoder) {
        if (action.payload.cpu_usage !== undefined) encoder.cpu_usage = action.payload.cpu_usage;
        if (action.payload.memory_usage !== undefined) encoder.memory_usage = action.payload.memory_usage;
        if (action.payload.current_job_id !== undefined) encoder.current_job_id = action.payload.current_job_id;
        encoder.last_heartbeat = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch encoders
      .addCase(fetchEncoders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEncoders.fulfilled, (state, action) => {
        state.loading = false;
        state.encoders = action.payload;
      })
      .addCase(fetchEncoders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch stats
      .addCase(fetchEncoderStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Create encoder
      .addCase(createEncoder.fulfilled, (state, action) => {
        state.encoders.push(action.payload);
      })
      // Check health
      .addCase(checkEncoderHealth.fulfilled, (state, action) => {
        const encoder = state.encoders.find(e => e.id === action.payload.encoderId);
        if (encoder) {
          encoder.is_online = action.payload.online;
          if (action.payload.stats) {
            encoder.cpu_usage = action.payload.stats.cpu_usage;
            encoder.memory_usage = action.payload.stats.memory_usage;
          }
        }
      })
      // Update encoder
      .addCase(updateEncoder.fulfilled, (state, action) => {
        const index = state.encoders.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.encoders[index] = action.payload;
        }
      });
  },
});

export const { clearEncodersError, updateEncoderStatus, updateEncoderStats } = encodersSlice.actions;
export default encodersSlice.reducer;